// Pages/Settings/Settings.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Settings as SettingsIcon, User, Bell, Shield, Building , Globe, Moon, Sun, Monitor, ExternalLink, Download, RefreshCw, X, Building2, Smartphone, Laptop } from 'lucide-react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations/translations';
import SendDashboardUrlComponent from '../ExternalPage/SendDashboardUrl';
import EditBuilding from '../Building/EditBuilding';
//import AdminDashboard from '../Admin/AdminDashboard';
import styles from './Settings.module.css';

const Settings = () => {
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const { language, changeLanguage } = useLanguage();
    const { t } = useTranslation(language);
    const location = useLocation();
    
    // Initialize activeTab based on location state or default to 'settings'
    const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'settings');
    const [settings, setSettings] = useState({
        notifications: true,
        emailNotifications: true,
        smsNotifications: false,
        autoBackup: true
    });

    // State for building/user data
    const [userData, setUserData] = useState(null);
    const [showDashboardUrlSender, setShowDashboardUrlSender] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    // Enhanced PWA installation states
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalling, setIsInstalling] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [installationStatus, setInstallationStatus] = useState('available'); // available, installing, installed, unavailable
    const [deviceInfo, setDeviceInfo] = useState({
        isMobile: false,
        isIOS: false,
        isAndroid: false,
        browser: ''
    });

    // Load user data from localStorage
    const loadUserData = () => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const user = JSON.parse(storedUser);
                setUserData(user);
                // Check if user is admin
                setIsAdmin(user.email === 'motisegev1@gmail.com');
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }
    };

    useEffect(() => {
        loadUserData();
    }, []);

    // Handle navigation state for activeTab when location changes
    useEffect(() => {
        if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);
            // Clear the location state after using it to prevent sticking
            window.history.replaceState({}, document.title);
        }
    }, [location.state?.activeTab]);

    // Listen for building changes to refresh user data
    useEffect(() => {
        const handleBuildingChange = () => {
            loadUserData();
        };

        window.addEventListener('buildingChanged', handleBuildingChange);
        return () => window.removeEventListener('buildingChanged', handleBuildingChange);
    }, []);

    // Detect device type and browser
    useEffect(() => {
        const userAgent = navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent);
        const isAndroid = /Android/.test(userAgent);
        const isMobile = isIOS || isAndroid || window.innerWidth <= 768;
        
        let browser = 'unknown';
        if (userAgent.includes('Chrome')) browser = 'chrome';
        else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'safari';
        else if (userAgent.includes('Firefox')) browser = 'firefox';
        else if (userAgent.includes('Edge')) browser = 'edge';

        setDeviceInfo({
            isMobile,
            isIOS,
            isAndroid,
            browser
        });

        // Check if already installed
        const checkIfInstalled = () => {
            // Check if running in standalone mode (PWA is installed)
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                               window.navigator.standalone === true;
            
            if (isStandalone) {
                setIsInstalled(true);
                setInstallationStatus('installed');
            }
        };

        checkIfInstalled();
    }, []);

    // Enhanced PWA installation event listeners
    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Save the event so it can be triggered later
            setDeferredPrompt(e);
            setInstallationStatus('available');
        };

        const handleAppInstalled = (e) => {
            setDeferredPrompt(null);
            setIsInstalled(true);
            setInstallationStatus('installed');
            showNotification('success', 'האפליקציה הותקנה בהצלחה! 🎉');
        };

        const handleVisibilityChange = () => {
            // Check if app became standalone (installed) when page becomes visible
            if (!document.hidden) {
                const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                                   window.navigator.standalone === true;
                if (isStandalone && !isInstalled) {
                    setIsInstalled(true);
                    setInstallationStatus('installed');
                }
            }
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isInstalled]);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && showDashboardUrlSender) {
                setShowDashboardUrlSender(false);
            }
        };

        if (showDashboardUrlSender) {
            document.addEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'unset';
        };
    }, [showDashboardUrlSender]);

    // Show notification helper
    const showNotification = (type, message, duration = 5000) => {
        // Create and show notification
        const notification = document.createElement('div');
        notification.className = `${styles.installNotification} ${type}`;
        notification.innerHTML = `
            <div class="${styles.notificationContent}">
                <span>${message}</span>
                <button onclick="this.parentElement.parentElement.remove()" class="${styles.notificationClose}">
                    ×
                </button>
            </div>
        `;
        document.body.appendChild(notification);

        // Auto remove after duration
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, duration);
    };

    // Enhanced PWA installation handler
    const handleInstallApp = async () => {

        // If already installed
        if (isInstalled) {
            showNotification('info', 'האפליקציה כבר מותקנת על המכשיר! 📱');
            return;
        }

        // If we have the install prompt (Chrome/Edge on Android/Desktop)
        if (deferredPrompt) {
            setIsInstalling(true);
            setInstallationStatus('installing');

            try {
                const result = await deferredPrompt.prompt();

                if (result.outcome === 'accepted') {
                    showNotification('success', 'האפליקציה מתקינה... אנא המתן');
                    // Note: 'appinstalled' event will handle the final success message
                } else {
                    showNotification('info', 'ההתקנה בוטלה על ידי המשתמש');
                    setInstallationStatus('available');
                }
            } catch (error) {
                showNotification('error', 'שגיאה בהתקנת האפליקציה. נסה שוב מאוחר יותר');
                setInstallationStatus('available');
            } finally {
                setIsInstalling(false);
                setDeferredPrompt(null);
            }
        } else {
            // Provide manual installation instructions based on device/browser
            let instructions = '';
            let duration = 8000;

            if (deviceInfo.isIOS) {
                if (deviceInfo.browser === 'safari') {
                    instructions = '📱 להתקנה: לחץ על כפתור השיתוף (⬆️) ובחר "הוסף למסך הבית"';
                } else {
                    instructions = '📱 להתקנה ב-iOS: פתח באפליקציית Safari ולחץ על כפתור השיתוף';
                }
            } else if (deviceInfo.isAndroid) {
                if (deviceInfo.browser === 'chrome') {
                    instructions = '📱 להתקנה: לחץ על התפריט (⋮) ובחר "הוסף למסך הבית" או "התקן אפליקציה"';
                } else {
                    instructions = '📱 להתקנה: לחץ על תפריט הדפדפן ובחר "הוסף למסך הבית"';
                }
            } else {
                // Desktop
                instructions = '💻 להתקנה: לחץ על כפתור התפריט בדפדפן ובחר "התקן אפליקציה" או "הוסף למסך הבית"';
            }

            showNotification('info', instructions, duration);
        }
    };

    // Get installation button text and icon
    const getInstallButtonContent = () => {
        if (isInstalled) {
            return {
                icon: <Smartphone size={16} />,
                text: 'אפליקציה מותקנת',
                disabled: true
            };
        }

        if (isInstalling) {
            return {
                icon: <RefreshCw size={16} className={styles.rotating} />,
                text: 'מתקין...',
                disabled: true
            };
        }

        if (deferredPrompt) {
            return {
                icon: <Download size={16} />,
                text: 'התקן אפליקציה',
                disabled: false
            };
        }

        return {
            icon: deviceInfo.isMobile ? <Smartphone size={16} /> : <Laptop size={16} />,
            text: 'הוראות התקנה',
            disabled: false
        };
    };

    // Get status description
    const getStatusDescription = () => {
        if (isInstalled) {
            return 'האפליקציה מותקנת ופועלת במצב עצמאי';
        }

        if (deferredPrompt) {
            return 'האפליקציה זמינה להתקנה על המכשיר שלך';
        }

        if (deviceInfo.isIOS && deviceInfo.browser !== 'safari') {
            return 'להתקנה ב-iOS, השתמש באפליקציית Safari';
        }

        return 'אפשר להתקין את האפליקציה לגישה מהירה יותר';
    };

    // Handler functions - SINGLE DECLARATIONS ONLY
    const handleSettingChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = () => {
        alert('הגדרות נשמרו בהצלחה!');
    };

    // Tab configuration
    const tabs = [
        {
            id: 'settings',
            label: t('settings'),
            icon: SettingsIcon
        },
        { 
            id: 'editBuilding', 
            label: language === 'he' ? 'ערוך בניין' : 'Edit Building', 
            icon: Building
        },
    ];

    // Settings content
    const renderSettingsContent = () => (
        <>
            <div className={styles.settingsGrid}>
                {/* System Settings */}
                <div className={styles.settingsCard}>
                    <div className={styles.cardHeader}>
                        <Shield size={20} className={styles.cardIcon} />
                        <h3 className={styles.cardTitle}>{t('systemSettings')}</h3>
                    </div>
                    <div className={styles.cardContent}>
                        <div className={styles.settingItem}>
                            <div className={styles.toggleContainer}>
                                <span className={styles.settingLabel}>
                                    {isDarkMode ? <Moon size={16} /> : <Sun size={16} />}
                                    {t('darkMode')}
                                </span>
                                <label className={styles.toggle}>
                                    <input
                                        type="checkbox"
                                        checked={isDarkMode}
                                        onChange={toggleDarkMode}
                                    />
                                    <span className={styles.toggleSlider}></span>
                                </label>
                            </div>
                        </div>
                        <div className={styles.settingItem}>
                            <label className={styles.settingLabel}>
                                <Globe size={16} />
                                {t('language')}
                            </label>
                            <select
                                className={styles.settingSelect}
                                value={language}
                                onChange={(e) => changeLanguage(e.target.value)}
                            >
                                <option value="he">{t('hebrew')}</option>
                                <option value="en">{t('english')}</option>
                            </select>
                        </div>
                        <div className={styles.settingItem}>
                            <div className={styles.toggleContainer}>
                                <span className={styles.settingLabel}>{t('autoBackup')}</span>
                                <label className={styles.toggle}>
                                    <input
                                        type="checkbox"
                                        checked={settings.autoBackup}
                                        onChange={(e) => handleSettingChange('autoBackup', e.target.checked)}
                                    />
                                    <span className={styles.toggleSlider}></span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced PWA Installation Section */}
                <div className={styles.settingsCard}>
                    <div className={styles.cardHeader}>
                        <Download size={20} className={styles.cardIcon} />
                        <h3 className={styles.cardTitle}>התקנת אפליקציה</h3>
                    </div>
                    <div className={styles.cardContent}>
                        <div className={styles.settingItem}>
                            <div className={styles.pwaSection}>
                                <div className={styles.pwaStatusIndicator}>
                                    <div className={`${styles.statusDot} ${styles[installationStatus]}`}></div>
                                    <span className={styles.statusText}>
                                        {getStatusDescription()}
                                    </span>
                                </div>
                                
                                <div className={styles.pwaDescription}>
                                    <p>התקן את בילדי על המכשיר שלך לחוויית שימוש מהירה ונוחה יותר:</p>
                                    <ul className={styles.benefitsList}>
                                        <li>🚀 טעינה מהירה יותר</li>
                                        <li>📱 פתיחה ישירה מהמסך הראשי</li>
                                        <li>🔔 התראות משופרות</li>
                                        <li>💾 עבודה גם ללא אינטרנט</li>
                                    </ul>
                                </div>

                                <div className={styles.deviceInfo}>
                                    <div className={styles.deviceInfoItem}>
                                        <span className={styles.deviceLabel}>מכשיר:</span>
                                        <span className={styles.deviceValue}>
                                            {deviceInfo.isMobile ? 
                                                (deviceInfo.isIOS ? 'iPhone/iPad' : 
                                                 deviceInfo.isAndroid ? 'Android' : 'Mobile') : 
                                                'Desktop'
                                            }
                                        </span>
                                    </div>
                                    <div className={styles.deviceInfoItem}>
                                        <span className={styles.deviceLabel}>דפדפן:</span>
                                        <span className={styles.deviceValue}>
                                            {deviceInfo.browser.charAt(0).toUpperCase() + deviceInfo.browser.slice(1)}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={handleInstallApp}
                                    className={`${styles.installAppButton} ${installationStatus === 'installed' ? styles.installed : ''}`}
                                    disabled={getInstallButtonContent().disabled}
                                    title={isInstalled ? 'האפליקציה כבר מותקנת' : 'התקן את האפליקציה על המכשיר'}
                                >
                                    {getInstallButtonContent().icon}
                                    {getInstallButtonContent().text}
                                </button>

                                {installationStatus === 'available' && !deferredPrompt && (
                                    <div className={styles.manualInstructions}>
                                        <div className={styles.instructionsHeader}>
                                            <span>הוראות התקנה ידנית:</span>
                                        </div>
                                        {deviceInfo.isIOS ? (
                                            <div className={styles.instructionSteps}>
                                                <p>📱 <strong>Safari בלבד:</strong></p>
                                                <p>1. לחץ על כפתור השיתוף <strong>⬆️</strong></p>
                                                <p>2. גלול למטה ובחר <strong>"הוסף למסך הבית"</strong></p>
                                                <p>3. לחץ <strong>"הוסף"</strong></p>
                                            </div>
                                        ) : deviceInfo.isAndroid ? (
                                            <div className={styles.instructionSteps}>
                                                <p>📱 <strong>Chrome/Edge:</strong></p>
                                                <p>1. לחץ על התפריט <strong>⋮</strong></p>
                                                <p>2. בחר <strong>"הוסף למסך הבית"</strong> או <strong>"התקן אפליקציה"</strong></p>
                                                <p>3. אשר את ההתקנה</p>
                                            </div>
                                        ) : (
                                            <div className={styles.instructionSteps}>
                                                <p>💻 <strong>Desktop:</strong></p>
                                                <p>1. לחץ על אייקון ההתקנה בכתובת</p>
                                                <p>2. או: תפריט דפדפן → "התקן אפליקציה"</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Dashboard Management Settings */}
                {userData && userData.buildingId && (
                    <div className={styles.settingsCard}>
                        <div className={styles.cardHeader}>
                            <Monitor size={20} className={styles.cardIcon} />
                            <h3 className={styles.cardTitle}>ניהול לוח בקרה חיצוני</h3>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.settingItem}>
                                <p className={styles.dashboardDescription}>
                                    שלח קישור ללוח הבקרה למסכי טלוויזיה חיצוניים.
                                    הקישור מאפשר צפייה בנתוני הבניין ללא צורך בהתחברות.
                                </p>
                            </div>
                            <div className={styles.settingItem}>
                                <div className={styles.dashboardInfo}>
                                    <div className={styles.buildingInfoItem}>
                                        <strong>בניין:</strong> {userData.selectedBuildingInfo?.buildingAddress || userData.selectedBuildingInfo?.address || userData.selectedBuildingInfo?.city || 'לא זמין'}
                                    </div>
                                    <div className={styles.buildingInfoItem}>
                                        <strong>מזהה:</strong> {userData.buildingId}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.settingItem}>
                                <button
                                    className={styles.dashboardButton}
                                    onClick={() => setShowDashboardUrlSender(!showDashboardUrlSender)}
                                >
                                    <ExternalLink size={16} />
                                    {showDashboardUrlSender ? 'סגור משלוח קישור' : 'שלח קישור לוח בקרה'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Dashboard URL Sender Modal */}
            {showDashboardUrlSender && userData && userData.buildingId && (
                <div className={styles.modalOverlay} onClick={() => setShowDashboardUrlSender(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>שליחת קישור לוח בקרה</h3>
                            <button
                                className={styles.modalCloseButton}
                                onClick={() => setShowDashboardUrlSender(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <SendDashboardUrlComponent
                                buildingId={userData.buildingId}
                                buildingAddress={userData.selectedBuildingInfo?.buildingAddress || userData.selectedBuildingInfo?.address || userData.selectedBuildingInfo?.city || 'לא זמין'}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.actions}>
                <button className={styles.saveButton} onClick={handleSave}>
                    {t('saveSettings')}
                </button>
                <button className={styles.cancelButton}>
                    {t('cancel')}
                </button>
            </div>
        </>
    );

    // Tab content rendering
    const renderTabContent = () => {
        switch (activeTab) {
            case 'editBuilding':
                return <EditBuilding userData={userData} />;
            case 'admin':
                return null;
            case 'settings':
            default:
                return renderSettingsContent();
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>
                    <SettingsIcon size={24} className={styles.titleIcon} />
                    {t('settingsTitle')}
                </h1>
                <p className={styles.subtitle}>{t('settingsSubtitle')}</p>
            </div>

            {/* Tabs Navigation */}
            <div className={styles.tabsContainer}>
                <div className={styles.tabsHeader}>
                    {tabs.map(tab => {
                        const IconComponent = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <IconComponent size={18} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>

                <div className={styles.tabContent}>
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};

export default Settings;